import { Injectable } from '@angular/core'

import { Bet, CalculatedExposureDto } from '../types/bet.type'

@Injectable({
	providedIn: 'root',
})
export class RiskExposureService {
	/**
	 * Main calculation method
	 */
	calculateExposure(bets: CalculatedExposureDto[], marketSelectionIds: Map<string, string[]>): number {
		try {
			const betModels = bets.map((betDto) => Bet.fromDto(betDto))
			// Calculate the true worst exposure using individual market methodology
			const worstExposure = this.calculateSumOfIndividualMarketExposures(betModels, marketSelectionIds)
			return worstExposure
		} catch (error) {
			console.error('Error calculating exposure:', error)
			return 0
		}
	}

	/**
	 * Calculate sum of individual market exposures with combination testing
	 */
	calculateSumOfIndividualMarketExposures(bets: Bet[], marketSelectionIds: Map<string, string[]>): number {
		const matchedBets = bets.filter((b) => b.size_matched > 0.0)
		const unmatchedBets = bets.filter((b) => b.size_matched === 0.0)

		// FIRST: Calculate exposure for current state (matched bets only)
		const currentStateExposure = this.calculateIndividualMarketExposureForCombination(matchedBets, marketSelectionIds)

		// SECOND: Test combinations of unmatched bets becoming matched
		if (unmatchedBets.length > 0) {
			const numUnmatched = unmatchedBets.length
			const totalCombinations = 1 << numUnmatched // 2^num_unmatched

			let maxCombinationExposure = 0.0

			for (let combination = 0; combination < totalCombinations; combination++) {
				const currentBets = [...matchedBets]

				// Add unmatched bets based on current combination
				for (let i = 0; i < numUnmatched; i++) {
					if ((combination >> i) & 1) {
						// This unmatched bet becomes matched
						const originalBet = unmatchedBets[i]
						const matchedBet = new Bet(
							originalBet.bet_id,
							originalBet.market_id,
							originalBet.selection_id,
							originalBet.side,
							originalBet.price,
							originalBet.bet_size,
							originalBet.bet_size, // Set size_matched to bet_size for matched bet
						)
						currentBets.push(matchedBet)
					}
				}

				// Calculate individual market exposure for this combination
				const combinationExposure = this.calculateIndividualMarketExposureForCombination(currentBets, marketSelectionIds)

				if (combinationExposure > maxCombinationExposure) {
					maxCombinationExposure = combinationExposure
				}
			}

			// Take the maximum of: current state vs. maximum of all combinations
			const finalExposure = Math.max(currentStateExposure, maxCombinationExposure)

			if (maxCombinationExposure > currentStateExposure) {
				console.log(`      Using maximum combination exposure: £${finalExposure.toFixed(2)}`)
			} else {
				console.log(`      Using current state exposure: £${finalExposure.toFixed(2)}`)
			}

			console.log(`    Total sum of individual market exposures: £${finalExposure.toFixed(2)}`)
			return finalExposure
		}

		// No unmatched bets, just return current state exposure
		return currentStateExposure
	}

	/**
	 * Calculate individual market exposure for a specific combination of bets
	 */
	calculateIndividualMarketExposureForCombination(bets: Bet[], marketSelectionIds: Map<string, string[]>): number {
		if (bets.length === 0) return 0.0

		// Group bets by market
		const markets = new Map<string, Bet[]>()
		for (const bet of bets) {
			if (!markets.has(bet.market_id)) {
				markets.set(bet.market_id, [])
			}
			markets.get(bet.market_id)!.push(bet)
		}

		let totalExposure = 0.0

		for (const [marketId, marketBets] of markets) {
			let worstCaseLoss = 0.0

			// Get all unique selections in this market
			// const selections = [...new Set(marketBets.map(b => b.selection_id))];
			const selections: string[] = marketSelectionIds.get(marketId) || []

			if (selections.length > 1) {
				// Multi-selection market - test only VALID outcome combinations
				// In a real market, exactly ONE selection wins and the others lose
				// eslint-disable-next-line
				for (let winnerIndex = 0; winnerIndex < selections.length; winnerIndex++) {
					const winningSelection = selections[winnerIndex]
					let netPlForCombination = 0.0

					// Create a map where only the current selection wins, all others lose
					const selectionOutcomes = new Map<string, boolean>()
					for (const selection of selections) {
						selectionOutcomes.set(selection, selection === winningSelection)
					}

					// Calculate net P&L across ALL bets for this combination
					for (const bet of marketBets) {
						const selectionWins = selectionOutcomes.get(bet.selection_id)

						let betPl: number
						if (selectionWins) {
							// This selection wins
							if (bet.side === 'back') {
								betPl = bet.potential_profit
							} else {
								betPl = -bet.potential_loss
							}
						} else {
							// This selection loses
							if (bet.side === 'back') {
								betPl = -bet.potential_loss
							} else {
								betPl = bet.potential_profit
							}
						}

						netPlForCombination += betPl
					}

					// Track the worst case (most negative value)
					if (netPlForCombination < worstCaseLoss) {
						worstCaseLoss = netPlForCombination
					}
				}

				// For all-BACK-bet scenarios, ensure we use the maximum of:
				// 1. Natural worst case (from scenario testing)
				// 2. Sum of potential losses (true worst case for all-BACK)
				// const allBackBets = marketBets.every(bet => bet.side === 'back');
				// if (allBackBets) {
				//     const sumOfLosses = -marketBets.reduce((sum, bet) => sum + bet.potential_loss, 0);
				//     if (sumOfLosses < worstCaseLoss) {
				//         worstCaseLoss = sumOfLosses;
				//         console.log(`          Applied all-BACK override: ${worstCaseLoss}`);
				//     }
				// } else if (worstCaseLoss === 0.0) {
				//     // Original fallback for mixed scenarios
				//     console.log(`          No negative scenarios found, keeping: ${worstCaseLoss}`);
				// }
			} else {
				// Single selection market - calculate win/lose scenarios
				const selection = selections[0]

				// Scenario 1: Selection wins
				let netPlIfSelectionWins = 0.0
				for (const bet of marketBets) {
					if (bet.selection_id === selection) {
						if (bet.side === 'back') {
							netPlIfSelectionWins += bet.potential_profit
						} else {
							netPlIfSelectionWins -= bet.potential_loss
						}
					}
				}

				// Scenario 2: Selection loses
				let netPlIfSelectionLoses = 0.0
				for (const bet of marketBets) {
					if (bet.selection_id === selection) {
						if (bet.side === 'back') {
							netPlIfSelectionLoses -= bet.potential_loss
						} else {
							netPlIfSelectionLoses += bet.potential_profit
						}
					}
				}

				// Track the worst case (most negative value)
				worstCaseLoss = Math.min(netPlIfSelectionWins, netPlIfSelectionLoses)
			}

			const marketExposure = Math.abs(worstCaseLoss)
			totalExposure += marketExposure
		}

		return totalExposure
	}
}
